import { useEffect, useState } from "react";
import { useHistory } from "react-router-dom";
import { useTranslation } from "react-i18next";
import {
  getContainerClientRegisteredInfo,
  getListRotationPeriods,
  getCharacterizationContainersList,
  getSuperContainerList,
} from "../../services/container";
import { getMonthsAgo } from "../../components/formatted/FormattedDateAndTime";
import { azureSASTokenToUrl } from "../../components/formatted/FormattedAzureSasToken";
import {
  formatCharacterizationDate,
  getCharacterizationStatus,
} from "../characterization-2/core/utils/utils";
import { parseClientData } from "../characterization-2/characterizationDetails/utils/clientUtils";
import { ReactComponent as PinIcon } from "../../static/assets/dashboard-pin.svg";
import { ReactComponent as CalendarIcon } from "../../static/assets/dashboard-calendar.svg";
import PillButton from "../../ui/components/PillButton";
import {
  buildCharacterizationReportQueryTargets,
  CHARACTERIZATION_REPORT_DEDUP_MS,
  isLeftCharacterizationZone,
  readBooleanStorage,
  writeBooleanStorage,
} from "./dashboardKpiUtils";

const IN_PROGRESS_STATUSES = new Set([
  "ImageEnTraitement",
  "ACompleter",
  "CorrectionEnTraitement",
]);
const STORAGE_KEY = "dashboard.characterizationsInProgressOpen";

const isLeftImage = (url) => /-(L)\./.test(url || "");

const getFlowName = (wasteCategory) => {
  if (wasteCategory == null) return "";
  if (typeof wasteCategory === "object") {
    return (
      wasteCategory.wasteCategoryName ||
      wasteCategory.label ||
      wasteCategory.name ||
      ""
    );
  }
  const trimmed = String(wasteCategory).trim();
  if (!trimmed || trimmed === "-") return "";
  try {
    const parsed = JSON.parse(trimmed);
    if (typeof parsed === "string") {
      return getFlowName(parsed);
    }
    if (parsed && typeof parsed === "object") {
      return parsed.wasteCategoryName || parsed.label || parsed.name || "";
    }
  } catch {
    return trimmed;
  }
  return trimmed;
};

const toPhotoSrc = (url) => {
  if (!url || typeof url !== "string") return null;
  try {
    const withQuery = url.includes("?") ? url : `${url}?`;
    return azureSASTokenToUrl(withQuery) || null;
  } catch {
    return url;
  }
};

const dedupeItems = (items) => {
  const kept = [];
  items
    .slice()
    .sort((a, b) => b.startTime - a.startTime)
    .forEach((item) => {
      const duplicate = kept.find(
        (existing) =>
          existing.groupId === item.groupId &&
          Math.abs(existing.startTime - item.startTime) <
            CHARACTERIZATION_REPORT_DEDUP_MS,
      );
      if (!duplicate) {
        kept.push(item);
        return;
      }
      if (item.isLeft && !duplicate.isLeft) {
        kept[kept.indexOf(duplicate)] = {
          ...item,
          flowName: item.flowName !== "—" ? item.flowName : duplicate.flowName,
          imageSrc: item.imageSrc || duplicate.imageSrc,
        };
        return;
      }
      if (duplicate.flowName === "—" && item.flowName !== "—") {
        duplicate.flowName = item.flowName;
      }
      if (!duplicate.imageSrc && item.imageSrc) {
        duplicate.imageSrc = item.imageSrc;
      }
    });
  return kept.sort((a, b) => b.startTime - a.startTime);
};

const mapPeriodForDetails = (period) => {
  let wasteCategory = null;
  if (period.waste_category && String(period.waste_category).trim() !== "") {
    try {
      wasteCategory = JSON.parse(period.waste_category);
    } catch {
      wasteCategory = null;
    }
  }
  return {
    ...period,
    periodMaterialStartSubfilter: period.start,
    periodMaterialStopSubfilter: period.stop,
    status: getCharacterizationStatus(period),
    clientParsed: parseClientData(period.client),
    startDate: period.start ? new Date(period.start) : null,
    wasteCategory,
  };
};

const DashboardCharacterizationCard = ({ item, t, onOpen }) => (
  <article className="dashboard-campaign-card dashboard-characterization-card">
    <div className="dashboard-characterization-photo">
      {item.imageSrc ? <img src={item.imageSrc} alt="" /> : null}
    </div>
    <div className="dashboard-characterization-body">
      <div className="dashboard-campaign-header">
        <p className="dashboard-campaign-address">{item.flowName}</p>
        <PillButton size="sm" onClick={() => onOpen(item)}>
          {t("dashboard.view")}
        </PillButton>
      </div>
      <div className="dashboard-campaign-dates">
        <CalendarIcon
          className="dashboard-campaign-calendar"
          aria-hidden="true"
        />
        <span>{item.dateLabel}</span>
      </div>
      {item.addressName ? (
        <div className="dashboard-campaign-city">
          <PinIcon className="dashboard-campaign-pin" aria-hidden="true" />
          <span>{item.addressName}</span>
        </div>
      ) : null}
    </div>
  </article>
);

export const DashboardCharacterizations = () => {
  const { t } = useTranslation();
  const history = useHistory();
  const [items, setItems] = useState([]);
  const [isOpen, setIsOpen] = useState(() =>
    readBooleanStorage(STORAGE_KEY, true),
  );

  const toggleOpen = () => {
    setIsOpen((current) => {
      const next = !current;
      writeBooleanStorage(STORAGE_KEY, next);
      return next;
    });
  };

  useEffect(() => {
    let cancelled = false;

    const load = async () => {
      try {
        // Same zone discovery as dashboard reports / WQ.
        const [superResponse, containersResponse] = await Promise.all([
          getSuperContainerList(),
          getCharacterizationContainersList({ characterizationOnly: false }),
        ]);
        if (cancelled) return;

        const allContainers = containersResponse?.data?.data ?? [];
        const superContainers = superResponse?.data?.superContainers ?? [];
        const superContainersById = new Map(
          superContainers.map((superContainer) => [
            String(superContainer._id),
            superContainer,
          ]),
        );
        const targets = buildCharacterizationReportQueryTargets(
          allContainers,
          superContainers,
        );
        if (targets.length === 0) {
          setItems([]);
          return;
        }

        const now = new Date();
        const start = getMonthsAgo(now, 12).toISOString();
        const stop = now.toISOString();

        const results = await Promise.allSettled(
          targets.map(async (target) => {
            const params = {
              periodMaterialStart: start,
              periodMaterialStop: stop,
              isCharacterization: true,
            };
            if (target.superContainerId) {
              params.superContainerId = target.superContainerId;
            }
            const response = await getListRotationPeriods(
              params,
              target.containerId,
            );
            return (response?.data?.periods ?? [])
              .map((period) => {
                const imageUrl = period.image_url || "";
                const mappedPeriod = mapPeriodForDetails(period);
                const superContainer = target.superContainerId
                  ? superContainersById.get(String(target.superContainerId)) ||
                    null
                  : null;
                return {
                  key: `${target.containerId}-${period.start}`,
                  groupId: String(target.zoneId || target.containerId),
                  containerId: target.containerId,
                  superContainerId: target.superContainerId,
                  period: mappedPeriod,
                  container: target.container,
                  superContainer,
                  containers: allContainers,
                  zone: target.container,
                  client: mappedPeriod.clientParsed,
                  periodStop: period.stop,
                  status: mappedPeriod.status,
                  flowName:
                    getFlowName(period.waste_category) ||
                    target.container.wasteName ||
                    "—",
                  dateLabel: period.start
                    ? formatCharacterizationDate(period.start)
                    : "—",
                  startTime: period.start
                    ? new Date(period.start).getTime()
                    : 0,
                  addressName: target.addressName || "",
                  imageSrc: toPhotoSrc(imageUrl),
                  isLeft:
                    isLeftCharacterizationZone(target.container) ||
                    isLeftImage(imageUrl),
                };
              })
              .filter((item) => IN_PROGRESS_STATUSES.has(item.status));
          }),
        );
        if (cancelled) return;

        const merged = dedupeItems(
          results
            .filter((result) => result.status === "fulfilled")
            .flatMap((result) => result.value),
        );

        const withFlux = await Promise.all(
          merged.map(async (item) => {
            if (item.flowName !== "—") return item;
            try {
              const time = item.periodStop
                ? new Date(item.periodStop)
                : new Date(item.startTime);
              if (Number.isNaN(time.getTime())) return item;
              const clientId = item.superContainerId || item.containerId;
              const response = await getContainerClientRegisteredInfo(
                {
                  start: new Date(time.getTime() - 1).toISOString(),
                  stop: new Date(time.getTime() + 1).toISOString(),
                },
                clientId,
              );
              const flowName = getFlowName(response?.data?.waste_category);
              return flowName ? { ...item, flowName } : item;
            } catch {
              return item;
            }
          }),
        );
        if (cancelled) return;
        setItems(withFlux);
      } catch {
        if (!cancelled) setItems([]);
      }
    };

    load();
    return () => {
      cancelled = true;
    };
  }, []);

  const handleOpen = (item) => {
    if (!item?.container?._id) return;
    history.push(`/characterization/details/${item.container._id}`, {
      period: item.period,
      client: item.client,
      container: item.container,
      superContainer: item.superContainer,
      containers: item.containers,
      zone: item.zone,
    });
  };

  const handleViewAll = () => {
    history.push("/characterization-areas");
  };

  return (
    <section className="dashboard-campaigns">
      <div className={`dashboard-sorting-header${isOpen ? " is-open" : ""}`}>
        <button
          type="button"
          className="dashboard-sorting-toggle"
          onClick={toggleOpen}
          aria-expanded={isOpen}
          aria-controls="dashboard-characterizations-in-progress-panel"
        >
          <h3 className="dashboard-campaigns-title">
            {t("Characterization in progress")} ({items.length})
          </h3>
          <i
            className={`fas fa-chevron-down dashboard-sorting-chevron${isOpen ? " is-open" : ""}`}
            aria-hidden="true"
          />
        </button>
      </div>
      {isOpen ? (
        <div id="dashboard-characterizations-in-progress-panel">
          {items.map((item) => (
            <DashboardCharacterizationCard
              key={item.key}
              item={item}
              t={t}
              onOpen={handleOpen}
            />
          ))}
          <PillButton
            size="md"
            className="dashboard-campaigns-all"
            onClick={handleViewAll}
          >
            {t("dashboard.viewAllCharacterizations")}
          </PillButton>
        </div>
      ) : null}
    </section>
  );
};
